/**
 * Centralized constants for OptiPlace scheduling system
 * Single source of truth for all scheduling-related thresholds and formulas
 */

// ============= WILLINGNESS TO REASSIGN GATES =============
// These formulas determine cross-tech reassignment thresholds based on shop's willingness slider (0-100)

/**
 * Maximum proficiency gap allowed for cross-tech reassignment
 * Formula: prof_gap_max(w) = 5 + 15·(w/100)
 * @param willingness - Shop's willingness_to_reassign value (0-100)
 * @returns Maximum proficiency gap in percentage points (5-20)
 * 
 * Examples:
 * - w=0:   5% gap max (very protective)
 * - w=50:  12.5% gap max (balanced)
 * - w=100: 20% gap max (aggressive reassignment)
 */
export function calculateProfGapMax(willingness: number): number {
  return 5 + 15 * (willingness / 100);
}

/**
 * Minimum time gain required to justify cross-tech reassignment
 * Formula: earlier_by_min(w) = 180 - 120·(w/100)
 * @param willingness - Shop's willingness_to_reassign value (0-100)
 * @returns Minimum minutes earlier (60-180)
 * 
 * Examples:
 * - w=0:   180 min earlier required (cautious)
 * - w=50:  120 min earlier required (moderate)
 * - w=100: 60 min earlier required (aggressive)
 */
export function calculateEarlierByMin(willingness: number): number {
  return 180 - 120 * (willingness / 100);
}

/**
 * Don't-steal window: recently moved tickets are protected
 * Formula: dont_steal_window(w) = 120 - 60·(w/100)
 * @param willingness - Shop's willingness_to_reassign value (0-100)
 * @returns Protection window in minutes (60-120)
 * 
 * Examples:
 * - w=0:   120 min protection (very protective)
 * - w=50:  90 min protection (balanced)
 * - w=100: 60 min protection (minimal protection)
 */
export function calculateDontStealWindow(willingness: number): number {
  return 120 - 60 * (willingness / 100);
}

// ============= SKILL TIER & PROFICIENCY =============

/**
 * Tier gap penalty multiplier
 * Penalizes mismatched skill tier assignments (both over-tier and under-tier)
 */
export const TIER_GAP_MULTIPLIER = 5.0;

/**
 * Minimum proficiency required for specialty bay assignments
 * Used in specialty bay validation (not hard-coded threshold)
 */
export const DEFAULT_SPECIALTY_MIN_PROFICIENCY = 70;

// ============= TIMER & BREAK CONSTANTS =============

/**
 * Warning threshold for timer not started
 * Show warning when timer should have started this many minutes ago
 */
export const TIMER_NOT_STARTED_WARNING_MIN = 15;

/**
 * Break grace period cap
 * Maximum break start slide = ½ of break duration
 */
export const BREAK_GRACE_CAP_FRACTION = 0.5;

/**
 * Next ticket replacement lookahead
 * Check for presence issues this many minutes before current ticket finishes
 */
export const NEXT_TICKET_LOOKAHEAD_MIN = 20;

// ============= COMPACTION & GAP THRESHOLDS =============

/**
 * Minimum gap before penalizing (minutes)
 * Gaps under this are considered acceptable
 */
export const ACCEPTABLE_GAP_MIN = 15;

/**
 * Gap penalty scaling factor
 * Larger value = heavier penalties for gaps
 */
export const GAP_PENALTY_SCALE = 5;

// ============= TPS (TICKET PROFICIENCY SCORE) WEIGHTS =============

/**
 * Job difficulty weights for TPS calculation
 * Maps skill level (1-10) to complexity weight
 * Higher skill = higher weight in proficiency calculation
 */
export const JOB_DIFFICULTY_WEIGHTS: Record<number, number> = {
  1: 0.25,  // Tires, basic maintenance
  2: 0.25,  // Inspections
  3: 0.50,  // Brakes
  4: 0.50,  // Steering & Suspension, Body & Frame
  5: 1.00,  // Alignment, Drivetrain
  6: 1.00,  // Engine Performance, HVAC, Starting & Charging
  7: 1.50,  // Transmission, Engine Mechanical, Restraints
  8: 1.50,  // Diagnostics, Electrical
  9: 2.00,  // ADAS, EV/Hybrid
  10: 2.00, // Master-level complexity
};

/**
 * Get difficulty weight for a job skill level
 * @param skillLevel - Job skill level (1-10)
 * @returns Complexity weight for TPS calculation
 */
export function getJobDifficultyWeight(skillLevel: number): number {
  return JOB_DIFFICULTY_WEIGHTS[skillLevel] || 1.0;
}

// ============= DEBUG FLAGS =============

/**
 * Enable verbose logging for Willingness gates
 */
export const DEBUG_WILLINGNESS_GATES = true;

/**
 * Enable verbose logging for TPS calculations
 */
export const DEBUG_TPS_SCORING = true;

/**
 * Enable verbose logging for EFS algorithm
 */
export const DEBUG_EFS_ALGORITHM = true;

/**
 * Enable verbose logging for live reshuffle events
 */
export const DEBUG_LIVE_RESHUFFLE = true;

/**
 * Enable verbose logging for auto-bump system
 * M-3: Replaces inline `const DEBUG = true` scattered through useAutoBump.ts
 */
export const DEBUG_AUTO_BUMP = false;
